import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEpcQtdFlag, isContractEpcQtd } from './useEpcQtdFlag';

export interface EvmContratoSnapshot {
  bac: number;
  pv: number;
  ev: number;
  ac: number;
  spi: number | null;
  cpi: number | null;
  eac: number | null;
  etc: number | null;
  vac: number | null;
}

/**
 * Fetches latest EVM snapshot for a single contract.
 * Only active when epc_qtd_enabled flag is ON and contract is EPC-type.
 * Returns null gracefully when data is unavailable (fallback behavior).
 */
export function useEvmContrato(contratoId: string, tipoContratoOperacional?: string, tipoMedicao?: string) {
  const { enabled: flagEnabled } = useEpcQtdFlag();
  const isEpc = isContractEpcQtd(flagEnabled, tipoContratoOperacional, tipoMedicao);

  const { data: evmSnapshot, isLoading } = useQuery({
    queryKey: ['evm-contrato-snapshot', contratoId],
    queryFn: async (): Promise<EvmContratoSnapshot | null> => {
      try {
        const { data, error } = await (supabase as any)
          .from('emp_vw_evm_contrato')
          .select('bac, pv, ev, ac, spi, cpi, eac, etc, vac')
          .eq('contrato_id', contratoId)
          .order('data', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error || !data) {
          console.debug('[EVM:CONTRATO] EVM_DATA_EMPTY for', contratoId);
          return null;
        }

        console.debug('[EVM:CONTRATO] EVM_SOURCE_SELECTED: epc_qtd');
        return {
          bac: data.bac || 0,
          pv: data.pv || 0,
          ev: data.ev || 0,
          ac: data.ac || 0,
          spi: data.spi,
          cpi: data.cpi,
          eac: data.eac,
          etc: data.etc,
          vac: data.vac,
        };
      } catch (err) {
        console.warn('[EVM:CONTRATO] Query exception, returning null:', err);
        return null;
      }
    },
    enabled: !!contratoId && isEpc,
    staleTime: 3 * 60 * 1000,
  });

  return {
    evmSnapshot: isEpc ? (evmSnapshot ?? null) : null,
    isEvmLoading: isLoading,
    isEpcQtd: isEpc,
  };
}
